import { canv } from "@/canv/global.js";
import { getTarget } from "@/canv/utils.js";
import type { DashType, LineOption, LineType, widgetOption } from "@/widgetMenu/widgetMenu";
import { wmBase } from "@/widgetMenu/wmBase";

export class WmLineType extends wmBase {
    constructor(options: widgetOption<LineOption>[]) {
        super();
        this.options = options;
    }

    build() {
        const uniqValues = this.getUniqs() as LineOption[];

        const view = this.lineTypes[uniqValues[0].lineType];

        this.currentLineWidth = uniqValues[0].lineWidth;
        this.currentLineType = uniqValues[0].lineType;
        this.currentDashType = uniqValues[0].dashType;

        return `
            <div class="wm_lineType">
                ${view}
            </div>
        `;
    }

    init() {
        const item = document.querySelector<HTMLElement>(".wm_lineType");
        item?.removeEventListener("click", this.showSubMenu, false);
        item?.addEventListener("click", this.showSubMenu, false);
    }

    showSubMenu = (e: Event) => {
        const subm = document.querySelector(".wm_lineType_submenu");
        if (subm) {
            subm.outerHTML = "";
            return;
        }
        const otherSubm = document.querySelector(".widgetSubMenu");
        if (otherSubm) {
            otherSubm.outerHTML = "";
        }

        canv.widgetMenu.subMenuBoss = "wm_lineType";
        const submenu = document.createElement("div");
        submenu.classList.add("widgetSubMenu");
        submenu.classList.add("wm_lineType_submenu");
        submenu.style.opacity = "0";
        document.body.prepend(submenu);

        submenu.innerHTML = this.getSubMenu();

        const types = document.querySelectorAll(".wm_lineType_submenu_types_item");
        for (let i = 0; i < types.length; i++) {
            types[i].addEventListener("click", this.typeClick.bind(this), false);
        }

        const dashes = document.querySelectorAll(".wm_lineType_submenu_dash_item");
        for (let i = 0; i < dashes.length; i++) {
            dashes[i].addEventListener("click", this.dashClick.bind(this), false);
        }

        const range = document.querySelector<HTMLElement>(".wm_lineType_range");
        range?.addEventListener("input", this.rangeChange.bind(this), false);

        setTimeout(canv.widgetMenu.TryLocateMenu.bind(canv.widgetMenu), 50);
    };

    getSubMenu() {
        const { currentLineType, currentDashType, currentLineWidth } = this;
        const selected = (cond: boolean) => (cond ? "wm_lineType_submenu_selected" : "");

        return `
            <input type="range" class="wm_lineType_range" value="${currentLineWidth}" min="1" max="15"/>
            <div class="wm_lineType_submenu_types">
                <div class="wm_lineType_submenu_types_item ${selected(currentLineType === "straight")}" type="straight">${this.lineTypes.straight}</div>
                <div class="wm_lineType_submenu_types_item ${selected(currentLineType === "quadratic")}" type="quadratic">${this.lineTypes.quadratic}</div>
                <div class="wm_lineType_submenu_types_item ${selected(currentLineType === "curved")}" type="curved">${this.lineTypes.curved}</div>
            </div>
            <div class="wm_lineType_submenu_dash">
                <div class="wm_lineType_submenu_dash_item ${selected(currentDashType === "solid")}" type="solid">${this.dashTypes.solid}</div>
                <div class="wm_lineType_submenu_dash_item ${selected(currentDashType === "dashed")}" type="dashed">${this.dashTypes.dashed}</div>
                <div class="wm_lineType_submenu_dash_item ${selected(currentDashType === "dotted")}" type="dotted">${this.dashTypes.dotted}</div>
            </div>
        `;
    }

    typeClick = (e: Event) => {
        const item = getTarget(e, "wm_lineType_submenu_types_item");
        const type = item?.getAttribute("type") as LineType;
        if (!type) return;

        const menuItem = document.querySelector<HTMLElement>(".wm_lineType");
        if (menuItem) menuItem.innerHTML = this.lineTypes[type];

        this.applyValue(this.options, { lineType: type }, false);
        this.currentLineType = type;

        document
            .querySelectorAll(".wm_lineType_submenu_types_item")
            .forEach((el) => el.classList.remove("wm_lineType_submenu_selected"));
        item.classList.add("wm_lineType_submenu_selected");
    };

    dashClick = (e: Event) => {
        const item = getTarget(e, "wm_lineType_submenu_dash_item");
        const type = item?.getAttribute("type") as DashType;
        if (!type) return;

        this.applyValue(this.options, { dashType: type }, false);
        this.currentDashType = type;

        document
            .querySelectorAll(".wm_lineType_submenu_dash_item")
            .forEach((el) => el.classList.remove("wm_lineType_submenu_selected"));
        item.classList.add("wm_lineType_submenu_selected");
    };

    rangeChange = (e: Event) => {
        const input = e.target as HTMLInputElement;
        const value = Number.parseInt(input.value, 10);

        this.applyValue(this.options, { lineWidth: value }, false);
        this.currentLineWidth = value;
    };

    name = "wm_lineType";
    options: widgetOption<LineOption>[] = [];
    currentLineWidth: LineOption["lineWidth"] = 0;
    currentLineType: LineOption["lineType"] = "curved";
    currentDashType: LineOption["dashType"] = "solid";
    lineTypes = {
        straight: `
                <svg width="23" height="23" viewBox="0 0 123 114" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="2.63695" y1="111.418" x2="120.637" y2="3.41815" stroke="black" stroke-width="7"/>
                </svg>
            `,
        quadratic: `
                <svg width="23" height="23" viewBox="0 0 112 124" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="3.5" y1="120.5" x2="58.5" y2="120.5" stroke="black" stroke-width="7" stroke-linecap="round"/>
                <line x1="59.5" y1="3.5" x2="108.5" y2="3.5" stroke="black" stroke-width="7" stroke-linecap="round"/>
                <line x1="59.5" y1="4.5" x2="59.5" y2="120.5" stroke="black" stroke-width="7" stroke-linecap="round"/>
                </svg>
            `,
        curved: `
                <svg width="23" height="23" viewBox="0 0 146 150" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M128.107 16.9167C17.6949 11.498 128.107 136.998 17.6949 132.715" stroke="black" stroke-width="7"/>
                <rect x="113" y="8" width="6" height="18" fill="white"/>
                <rect x="27" y="123" width="6" height="18" fill="white"/>
                </svg>
            `,
    };
    dashTypes = {
        solid: `
                <svg width="23" height="23" viewBox="0 0 104 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line y1="3.5" x2="104" y2="3.5" stroke="black" stroke-width="7"/>
                </svg>
            `,
        dashed: `
                <svg width="23" height="23" viewBox="0 0 104 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line y1="3.5" x2="104" y2="3.5" stroke="black" stroke-width="7" stroke-dasharray="14 14"/>
                </svg>
            `,
        dotted: `
                <svg width="23" height="23" viewBox="0 0 104 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="3.5" y1="3.5" x2="100.5" y2="3.5" stroke="black" stroke-width="7" stroke-linecap="round" stroke-dasharray="0.1 15"/>
                </svg>
            `,
    };
}
